import React, { useState } from "react";
import Pie from "@visx/shape/lib/shapes/Pie";
import { scaleOrdinal } from "@visx/scale";
import { Group } from "@visx/group";
import { animated, useTransition, interpolate } from "@react-spring/web";
import {
  backgroundLight,
  color2,
  color3,
  color4,
  color5,
  color6,
  textSecondary,
} from "../../../../utils/colors";
import { useTranslation } from "react-i18next";
import useThemeColors from "../../../../hooks/useThemeColors";
// import useCurrencySymbol from "../../../../hooks/useCurrencySymbol";

// Dynamic colors for treatment plan statuses
const treatmentPlanStatuses = [
  "planning",
  "approved",
  "ongoing",
  "completed",
  "cancelled",
];

const statusColors = scaleOrdinal({
  domain: treatmentPlanStatuses,
  range: [color2, color3, color4, color5, color6],
});

const defaultMargin = { top: 20, right: 20, bottom: 20, left: 20 };

const TreatmentPlansPieChart = ({
  width,
  height,
  margin = defaultMargin,
  animate = true,
  treatmentPlans,
  currency,
}) => {
  const { t } = useTranslation();

  const { textPrimaryMode } = useThemeColors();

  const [selectedTreatmentPlanStatus, setSelectedTreatmentPlanStatus] =
    useState(null);

  // If width is too small, don't render the chart
  if (width < 10 || !treatmentPlans) return null;

  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;
  const radius = Math.min(innerWidth, innerHeight) / 2;
  const centerY = innerHeight / 2;
  const centerX = innerWidth / 2;
  const donutThickness = 50;

  // Prepare data for pie chart based on treatmentPlans
  const data = treatmentPlanStatuses.map((status) => ({
    label: t(`treatmentPlanStatus.${status}`),
    count: treatmentPlans[status]?.count || 0, // Safely handle missing data
  }));

  // Compute total count for display in the center
  const totalCount = data.reduce((acc, curr) => acc + curr.count, 0);

  // Determine the text and count to display in the center
  const centerText = selectedTreatmentPlanStatus || t("common.total");
  const centerCount = selectedTreatmentPlanStatus
    ? data.find((d) => d.label === selectedTreatmentPlanStatus)?.count || 0
    : totalCount;

  // console.log("treatmentPlans", treatmentPlans);
  // console.log("data", data);
  // console.log("totalCount", totalCount);

  // Pie chart rendering
  return (
    <>
      <svg width={width} height={height}>
        <Group top={centerY + margin.top} left={centerX + margin.left}>
          {/* Grey background donut for zero data */}
          {totalCount === 0 && (
            <circle
              r={radius - donutThickness / 2}
              fill="none"
              stroke={backgroundLight} // Use a grey color
              strokeWidth={donutThickness}
            />
          )}

          {/* Render the pie chart */}
          <Pie
            data={
              selectedTreatmentPlanStatus
                ? data.filter(
                    ({ label }) => label === selectedTreatmentPlanStatus
                  )
                : data
            }
            pieValue={(d) => d.count}
            outerRadius={radius}
            innerRadius={radius - donutThickness}
            cornerRadius={3}
            padAngle={0.005}
          >
            {(pie) => (
              <>
                <AnimatedPie
                  {...pie}
                  animate={animate}
                  getKey={(arc) => arc.data.label}
                  onClickDatum={({ data: { label } }) =>
                    animate &&
                    setSelectedTreatmentPlanStatus(
                      selectedTreatmentPlanStatus === label ? null : label
                    )
                  }
                  getColor={(arc) => statusColors(arc.data.label)}
                />
              </>
            )}
          </Pie>

          <text
            x={0}
            y={-10}
            dy=".33em"
            fill={textSecondary}
            fontWeight="500"
            fontSize={12}
            textAnchor="middle"
            pointerEvents="none"
          >
            {centerText}
          </text>
          <text
            x={0}
            y={10}
            dy=".33em"
            fill={textPrimaryMode}
            fontWeight="600"
            fontSize={18}
            textAnchor="middle"
            pointerEvents="none"
          >
            {centerCount}
          </text>
        </Group>
      </svg>
    </>
  );
};

// react-spring transition definitions
const fromLeaveTransition = ({ endAngle }) => ({
  startAngle: endAngle > Math.PI ? 2 * Math.PI : 0,
  endAngle: endAngle > Math.PI ? 2 * Math.PI : 0,
  opacity: 0,
});
const enterUpdateTransition = ({ startAngle, endAngle }) => ({
  startAngle,
  endAngle,
  opacity: 1,
});

function AnimatedPie({ animate, arcs, path, getKey, getColor, onClickDatum }) {
  const transitions = useTransition(arcs, {
    from: animate ? fromLeaveTransition : enterUpdateTransition,
    enter: enterUpdateTransition,
    update: enterUpdateTransition,
    leave: animate ? fromLeaveTransition : enterUpdateTransition,
    keys: getKey,
  });
  const [hoveredArc, setHoveredArc] = useState(null);

  return transitions((props, arc, { key }) => {
    const [centroidX, centroidY] = path.centroid(arc);
    const hasSpaceForLabel = arc.endAngle - arc.startAngle >= 0.1;

    return (
      <>
        <g key={key}>
          <animated.path
            d={interpolate(
              [props.startAngle, props.endAngle],
              (startAngle, endAngle) =>
                path({
                  ...arc,
                  startAngle,
                  endAngle,
                })
            )}
            fill={getColor(arc)}
            onClick={() => onClickDatum(arc)}
            onTouchStart={() => onClickDatum(arc)}
            onMouseEnter={() => setHoveredArc(arc)} // Track hover start
            onMouseLeave={() => setHoveredArc(null)} // Track hover end
            style={{
              cursor: "pointer",
              transform: hoveredArc === arc ? "scale(1.05)" : "scale(1)", // Apply scaling on hover
              transition: "transform 0.2s ease", // Smooth scaling transition
            }}
          />
          {hasSpaceForLabel && (
            <animated.g style={{ opacity: props.opacity }}>
              <text
                fill="white"
                x={centroidX}
                y={centroidY}
                dy=".33em"
                fontSize={9}
                textAnchor="middle"
                pointerEvents="none"
              >
                {getKey(arc)}
              </text>
            </animated.g>
          )}
        </g>
      </>
    );
  });
}

export default TreatmentPlansPieChart;
